import { ReactComponent as BaselineDrawIcon } from "@web-src/images/icons/baseline-draw.svg";
import { ReactComponent as TextIcon } from "@web-src/images/icons/text.svg";
import { ReactComponent as CropBoldIcon } from "@web-src/images/icons/crop-bold.svg";
import { Transition } from "@headlessui/react";
import { useTranslations } from "@jugl-web/utils";
import { useMemo, useRef, useState, useLayoutEffect } from "react";
import useEditorContext from "./useEditorContext";
import BrushTool from "../tools/BrushTool";
import ToolbarAction, { Action } from "./Toolbar/ToolbarAction";
import ToolbarActionBrush from "./Toolbar/ToolbarActionBrush";
import ToolbarActionWrapper from "./Toolbar/ToolbarActionWrapper";
import ToolbarActionText from "./Toolbar/ToolbarActionText";
import TextTool from "../tools/TextTool";
import ToolbarCaption from "./Toolbar/ToolbarCaption";
import ToolbarSubmitButton from "./Toolbar/ToolbarSubmitButton";
import TransformTool from "../tools/TransformTool";
import { ToolbarActionTransform } from "./Toolbar/ToolbarActionTransform";

const EditorToolbar: React.FC<{ hideCaptionInput?: boolean }> = ({
  hideCaptionInput,
}) => {
  const { instance, activeTool } = useEditorContext();
  const { t } = useTranslations();
  const previewRef = useRef<HTMLDivElement>(null);
  const toolkitRef = useRef<HTMLDivElement>(null);
  const captionRef = useRef<HTMLDivElement>(null);
  const [containerHeight, setContainerHeight] = useState<number>(0);

  const toolbarActions: Action[] = useMemo(() => {
    const canvas = instance?.canvas ?? null;

    return [
      {
        name: "crop",
        tooltip: t({
          id: "chats-page.crop-image",
          defaultMessage: "Crop Image",
        }),
        icon: <CropBoldIcon />,
        tool: new TransformTool(canvas),
        toolkit: <ToolbarActionTransform />,
        disabled: false,
      },
      {
        name: "brush",
        tooltip: t({
          id: "chats-page.draw-image",
          defaultMessage: "Draw an image",
        }),
        icon: <BaselineDrawIcon />,
        tool: new BrushTool(canvas),
        toolkit: <ToolbarActionBrush />,
        disabled: false,
      },
      {
        name: "text",
        tooltip: t({
          id: "chats-page.add-text-on-image",
          defaultMessage: "Add Text on Image",
        }),
        icon: <TextIcon />,
        tool: new TextTool(canvas),
        toolkit: <ToolbarActionText />,
        disabled: false,
      },
    ] as const;
  }, [instance, t]);

  useLayoutEffect(() => {
    const updateDimensions = () => {
      if (activeTool === "preview" && previewRef.current) {
        const textareaHeight = captionRef.current?.offsetHeight ?? 0;

        setContainerHeight(previewRef.current.offsetHeight + textareaHeight);
      } else if (activeTool !== "preview" && toolkitRef.current) {
        setContainerHeight(toolkitRef.current.offsetHeight);
      }
    };

    updateDimensions();

    const timeoutId = setTimeout(updateDimensions, 50);
    return () => clearTimeout(timeoutId);
  }, [activeTool]);

  return (
    <div
      className="relative mx-auto w-screen max-w-2xl rounded-2xl bg-[#121516] px-5 transition-all duration-200"
      style={{
        height: containerHeight > 0 ? `${containerHeight}px` : "auto",
      }}
    >
      <Transition
        show={activeTool === "preview"}
        enter="transition-opacity duration-200 ease-in delay-200"
        enterFrom="opacity-0"
        enterTo="opacity-100"
        leave="transition-opacity duration-200 ease-out"
        leaveFrom="opacity-100"
        leaveTo="opacity-0"
      >
        <div className="absolute top-0 left-0 w-full">
          <div
            ref={previewRef}
            className="box-content flex items-center justify-between gap-4 p-4"
          >
            <div className="flex gap-4">
              {toolbarActions.map((action) => (
                <ToolbarAction key={action.name} action={action} />
              ))}
            </div>

            <ToolbarSubmitButton type="icon" />
          </div>

          {!hideCaptionInput && (
            <div className="px-4 pb-5 pt-0" ref={captionRef}>
              <ToolbarCaption />
            </div>
          )}
        </div>
      </Transition>

      <Transition
        show={activeTool !== "preview"}
        enter="transition-opacity duration-200 ease-in delay-200"
        enterFrom="opacity-0"
        enterTo="opacity-100"
        leave="transition-opacity duration-200 ease-out"
        leaveFrom="opacity-100"
        leaveTo="opacity-0"
      >
        <div className="absolute top-0 left-0 w-full">
          <div
            ref={toolkitRef}
            className="box-content flex items-center gap-4 p-4"
          >
            {toolbarActions.map((action) => {
              const isActive = activeTool === action.name;
              const hasToolkit = action.toolkit;

              if (!isActive || !hasToolkit) return null;

              return (
                <ToolbarActionWrapper key={action.name}>
                  {action.toolkit}
                </ToolbarActionWrapper>
              );
            })}
          </div>
        </div>
      </Transition>
    </div>
  );
};

export default EditorToolbar;
